import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"
import { CircleHelp, Info } from "lucide-react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

const labelVariants = cva([
  "text-base font-semibold text-coval-text text-sm",
  "peer-disabled:opacity-50",
])

export interface LabelProps
  extends
    React.LabelHTMLAttributes<HTMLLabelElement>,
    VariantProps<typeof labelVariants> {
  tooltip?: React.ReactNode
  /** Icon that opens the tooltip. "help" is a question mark, "info" an i. */
  tooltipIcon?: "help" | "info"
  description?: React.ReactNode
  descriptionId?: string
}

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  (
    {
      className,
      children,
      tooltip,
      tooltipIcon = "help",
      description,
      descriptionId: descriptionIdProp,
      ...props
    },
    ref,
  ) => {
    const TooltipIcon = tooltipIcon === "info" ? Info : CircleHelp
    const generatedId = React.useId()
    const descriptionId = descriptionIdProp ?? generatedId

    return (
      <label
        className={cn(
          labelVariants(),
          description
            ? "inline-flex flex-col items-start gap-1"
            : "inline-flex items-center gap-1",
          className,
        )}
        ref={ref}
        {...props}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {tooltip && (
            <Tooltip content={tooltip}>
              <button
                type="button"
                onClick={(e) => e.preventDefault()}
                aria-label="Help"
              >
                <TooltipIcon className="size-3.5 text-coval-text-muted" />
              </button>
            </Tooltip>
          )}
        </span>
        {description && (
          <span id={descriptionId} className="font-normal">
            {description}
            {tooltip && <span className="sr-only"> {tooltip}</span>}
          </span>
        )}
      </label>
    )
  },
)

Label.displayName = "Label"

export { Label, labelVariants }
